// Ported from dev-scrips-pm-react src/shared/lib/warn-duplicate-keys.ts (PROD-1119 Patients data layer).
/**
 * Dev-only guard against the "two children with the same key" React bug.
 *
 * Background: master-data lookups that ship the FHIR `Code` shape
 * (`{ code, display, definition }`) were briefly consumed as `{ id, name }`
 * — every option keyed by `undefined`, producing ~80 duplicate-key console
 * errors + blank labels (PR #453 review). The fix keys by `code`; this
 * guard makes a regression LOUD instead of silent: if a lookup ever ships
 * rows whose key collides (or is null), it warns once during the fetch.
 *
 * No-op in production (gated on `import.meta.env.DEV`) — zero runtime cost
 * for users, and never throws (a data quirk shouldn't break the UI).
 */
export function warnDuplicateKeys<T>(
  items: T[],
  keyOf: (item: T) => string | null | undefined,
  label: string,
): T[] {
  if (import.meta.env.DEV && items.length > 0) {
    const keys = items.map(keyOf);
    const nullish = keys.filter((k) => k == null || k === '').length;
    const unique = new Set(keys).size;
    if (nullish > 0 || unique !== keys.length) {
      // eslint-disable-next-line no-console
      console.warn(
        `[lookup:${label}] ${items.length} rows but only ${unique} unique keys` +
          (nullish > 0 ? ` (${nullish} null/empty)` : '') +
          ' — React keys will collide. Are consumers keying by the wrong field?',
      );
    }
  }
  return items;
}
